import React, { useEffect, useState } from "react";
import { apiClient } from "../lib/api";
import StockCard from "../components/StockCard";
import ScoreBreakdownModal from "../components/ScoreBreakdownModal";
import InsightModal from "../components/InsightModal";
import { Star, Loader2, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";

function Column({ title, items, accent, onToggleFav, onOpenScore, onOpenInsight, emptyHint }) {
  const isBuy = accent === "buy";
  return (
    <section
      className={`rounded-md border ${isBuy ? "border-buy" : "border-sell"}`}
      data-testid={`favorites-column-${accent}`}
    >
      <header
        className={`${isBuy ? "bg-buy-soft text-buy-strong" : "bg-sell-soft text-sell-strong"} px-5 py-4 flex items-center justify-between border-b ${isBuy ? "border-buy" : "border-sell"}`}
      >
        <div className="flex items-center gap-3">
          {isBuy ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
          <h2 className="font-display text-lg font-bold tracking-tight">{title}</h2>
        </div>
        <span className="text-[10px] uppercase tracking-[0.25em]">{items.length} ações</span>
      </header>
      <div className="p-4 grid grid-cols-1 xl:grid-cols-2 gap-4 min-h-[200px]">
        {items.length === 0 ? (
          <div className="col-span-full flex flex-col items-center justify-center text-center text-sm text-muted-foreground py-10">
            <Star className="w-6 h-6 mb-3 opacity-50" />
            <p className="max-w-xs">{emptyHint}</p>
          </div>
        ) : (
          items.map((s) => (
            <StockCard
              key={s.id}
              stock={s}
              onToggleFav={onToggleFav}
              onOpenScore={onOpenScore}
              onOpenInsight={onOpenInsight}
            />
          ))
        )}
      </div>
    </section>
  );
}

export default function Favorites() {
  const [data, setData] = useState({ buy: [], sell: [], neutral: [] });
  const [loading, setLoading] = useState(true);
  const [scoreStock, setScoreStock] = useState(null);
  const [insightStock, setInsightStock] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/favorites");
      setData(r.data || { buy: [], sell: [], neutral: [] });
    } catch {
      toast.error("Não foi possível carregar os favoritos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleToggleFav = async (stock) => {
    try {
      await apiClient.post("/favorites/toggle", { stock_id: stock.id });
      load();
    } catch {
      toast.error("Falha ao atualizar favorito.");
    }
  };

  return (
    <div className="px-6 md:px-12 py-8 max-w-[1600px] mx-auto" data-testid="favorites-page">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">
          Espaço 3 · Seus favoritos
        </div>
        <h1 className="font-display text-4xl font-bold tracking-tight leading-none">
          Favoritos · Compra & Venda
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          Suas ações marcadas, organizadas pelo motor de recomendação em duas colunas:
          quando a recomendação muda, a ação migra automaticamente.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-12">
          <Loader2 className="w-4 h-4 animate-spin" /> Carregando favoritos…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 fade-up">
            <Column
              title="Compra"
              accent="buy"
              items={data.buy || []}
              onToggleFav={handleToggleFav}
              onOpenScore={setScoreStock}
              onOpenInsight={setInsightStock}
              emptyHint="Nenhuma ação favorita classificada como compra hoje. Adicione favoritos pela listagem ou top 12."
            />
            <Column
              title="Venda"
              accent="sell"
              items={data.sell || []}
              onToggleFav={handleToggleFav}
              onOpenScore={setScoreStock}
              onOpenInsight={setInsightStock}
              emptyHint="Nenhum favorito com recomendação de venda hoje."
            />
          </div>

          {(data.neutral || []).length > 0 && (
            <section className="mt-8" data-testid="favorites-neutral">
              <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">
                Favoritos neutros (sem viés direcional)
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {data.neutral.map((s) => (
                  <StockCard
                    key={s.id}
                    stock={s}
                    onToggleFav={handleToggleFav}
                    onOpenScore={setScoreStock}
                    onOpenInsight={setInsightStock}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}

      <ScoreBreakdownModal
        open={!!scoreStock}
        stock={scoreStock}
        onClose={() => setScoreStock(null)}
      />
      <InsightModal
        open={!!insightStock}
        stock={insightStock}
        onClose={() => setInsightStock(null)}
      />
    </div>
  );
}
